export { ExtraCoverageAccessor } from "./extraCoverages/extraCoverageAccessor";
export { PackageCoverageAccessor } from "./packageCoverages/packageCoverageAccessor";
export { SettlementOption } from "./packageCoverages/options/settlementOption";
export { getExtraCoveragesMap } from "./extraCoverages/extraCoveragesMap";
export { getPackageCoveragesMap } from "./packageCoverages/packageCoveragesMap";
export {
  extraCoverageKeyToAccessorMap,
  extraCoveragesPageAccessors,
  compareExtraCoveragesOnPage,
} from "./utils";

export { MineSubsidenceOption } from "./extraCoverages/options/mineSubsidence/option";
export { getMineSubsidenceOption } from "./extraCoverages/options/mineSubsidence/getOption";
export { MineSubsidenceScheduledOption } from "./extraCoverages/options/mineSubsidenceScheduled/option";
export { getMineSubsidenceScheduledOption } from "./extraCoverages/options/mineSubsidenceScheduled/getOption";
export { RoofPaymentScheduleOption } from "./extraCoverages/options/roofPaymentSchedule/option";
export { getRoofPaymentScheduleOption } from "./extraCoverages/options/roofPaymentSchedule/getOption";

export {
  ScreenWithCoverages,
  CoverageLevel,
  type Coverage,
  type CoverageAccessor,
  type CoverageOptions,
  type CoverageValueFormats,
  type GetRequiredWithOptions,
  type GetOptionalWithOptions,
} from "./types";

import { uniq } from "lodash";

import { ExtraCoverageAccessor } from "./extraCoverages/extraCoverageAccessor";
import { PackageCoverageAccessor } from "./packageCoverages/packageCoverageAccessor";
import { ExtensionCoverageAccessor, CoverageAccessor } from "./types";

/**
 * Accessors for Coverage Packages with settlement option
 */
const settlementCoverageAccessors: CoverageAccessor[] = [
  PackageCoverageAccessor.HomeCoverageSettlementType,
  PackageCoverageAccessor.YourBelongingsSettlementType,
  PackageCoverageAccessor.OtherStructuresSettlementType,
];

/**
 * Accessors for Coverage Packages but only the customizable ones.
 * Those are in order as rendered on the Edit My Plan page.
 */
const customizablePackageCoverageAccessors: CoverageAccessor[] = [
  PackageCoverageAccessor.HomeCoverage,
  PackageCoverageAccessor.HomeCoverageSettlementType,
  PackageCoverageAccessor.YourBelongings,
  PackageCoverageAccessor.YourBelongingsSettlementType,
  PackageCoverageAccessor.PersonalLiability,
  PackageCoverageAccessor.PremisesLiability,
  PackageCoverageAccessor.LossOfUse,
  PackageCoverageAccessor.StandardDeductible,
  PackageCoverageAccessor.WindHail,
  PackageCoverageAccessor.OtherStructures,
  PackageCoverageAccessor.OtherStructuresSettlementType,
  PackageCoverageAccessor.WaterDamage,
  PackageCoverageAccessor.MedicalPayments,
];

/**
 * Coverage keys (e.g. `coverageA`) of the customizable package coverages, in render order.
 * Can be replaced by Set after upgrading target to at least es2015 (es6).
 */
const customizablePackageCoverageKeys = uniq(
  customizablePackageCoverageAccessors.map((accessor) => accessor.split(".")[0])
);

/**
 * Accessors for Coverage Packages but only the non-customizable ones.
 */
const nonCustomizablePackageCoverageAccessors: CoverageAccessor[] = [
  PackageCoverageAccessor.WaterDamageDeductible,
  PackageCoverageAccessor.InflationGuard,
  PackageCoverageAccessor.RoofPaymentSchedule,
];

/**
 * Accessors for Coverage Packages
 */
const packageCoverageAccessors: CoverageAccessor[] = [
  ...customizablePackageCoverageAccessors,
  ...nonCustomizablePackageCoverageAccessors,
];

/**
 * Accessors for Coverage Extensions
 */
const extensionCoverageAccessorts: CoverageAccessor[] = [
  ExtensionCoverageAccessor.AnimalLiability,
  ExtensionCoverageAccessor.DamageToPropertyOfOthers,
  ExtensionCoverageAccessor.FungiWetOrDryRotOrBacteriaProperty,
];

/**
 * Accessors for Extra Coverages.
 * Those are in order as rendered on the Edit My Plan page.
 */
const extraCoverageAccessors: CoverageAccessor[] = [
  ExtraCoverageAccessor.MineSubsidence,
  ExtraCoverageAccessor.MineSubsidenceScheduled,
  ExtraCoverageAccessor.DebrisRemoval,
  ExtraCoverageAccessor.IncidentalFarming,
  ExtraCoverageAccessor.LandlordPersonalInjury,
  ExtraCoverageAccessor.LossAssessment,
  ExtraCoverageAccessor.ValuableItemProtection,
  ExtraCoverageAccessor.GolfCartDamageAndLiabilityCoverage,
  ExtraCoverageAccessor.EnhancedCoverage,
  ExtraCoverageAccessor.TripCollisionCoverage,
  ExtraCoverageAccessor.EarthquakeCoverage,
  ExtraCoverageAccessor.VacationRentalCoverage,
  ExtraCoverageAccessor.WaterBackupCoverage,
  ExtraCoverageAccessor.EquipmentBreakdown,
  ExtraCoverageAccessor.SinkholeCoverage,
  ExtraCoverageAccessor.ResidenceBurglary,
  ExtraCoverageAccessor.RoofPaymentSchedule,
  ExtraCoverageAccessor.BuildersRisk,
  ExtraCoverageAccessor.SecondaryResidenceLiability,
  ExtraCoverageAccessor.VandalismOrMaliciousMischief,
  ExtraCoverageAccessor.WindstormOrHailExclusion,
  ExtraCoverageAccessor.FortifiedRoofUpgrade,
  ExtraCoverageAccessor.IdentityFraudExpense,
];

/**
 * Accessors for Coverage Packages, Extra Coverages and Coverage Extensions.
 */
const coverageAccessors: CoverageAccessor[] = [
  ...packageCoverageAccessors,
  ...extensionCoverageAccessorts,
  ...extraCoverageAccessors,
];

export {
  coverageAccessors,
  packageCoverageAccessors,
  extraCoverageAccessors,
  extensionCoverageAccessorts,
  settlementCoverageAccessors,
  customizablePackageCoverageAccessors,
  customizablePackageCoverageKeys,
  nonCustomizablePackageCoverageAccessors,
};
